import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Add as AddIcon, EnergySavingsLeaf as LeafIcon } from '@mui/icons-material';
import { quoteRequestsApi } from '../services/api.ts';
import {
  QuoteRequestCreate,
  QuoteRequestProductInterest,
  PRODUCT_INTEREST_LABELS,
  QUOTE_REQUEST_STATUS_LABELS,
  QUOTE_REQUEST_STATUS_COLORS,
} from '../types/index.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

const PRODUCT_OPTIONS = Object.values(QuoteRequestProductInterest).map((v) => ({
  value: v,
  label: PRODUCT_INTEREST_LABELS[v],
}));

const extractError = (e: any): string => {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d.msg).join('; ');
  }
  return String(detail || 'Something went wrong');
};

const CustomerPortalPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<QuoteRequestCreate>({
    first_name: user?.first_name ?? '',
    last_name: user?.last_name ?? '',
    email: user?.email ?? '',
    phone: '',
    address_line_1: '',
    city: '',
    postcode: '',
    product_interest: QuoteRequestProductInterest.HEAT_PUMP,
    message: '',
  });
  const [formError, setFormError] = useState('');

  const { data, isLoading } = useQuery(
    ['my-quote-requests'],
    quoteRequestsApi.getMyRequests,
    { refetchOnWindowFocus: true }
  );

  const createMutation = useMutation(quoteRequestsApi.createAuthenticated, {
    onSuccess: () => {
      queryClient.invalidateQueries(['my-quote-requests']);
      setDialogOpen(false);
      setFormError('');
      setForm((f) => ({ ...f, address_line_1: '', city: '', postcode: '', message: '' }));
    },
    onError: (e: any) => setFormError(extractError(e)),
  });

  const set = (k: keyof QuoteRequestCreate, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    createMutation.mutate({
      ...form,
      phone: form.phone || undefined,
      message: form.message || undefined,
    });
  };

  const requests = data?.requests ?? [];

  return (
    <Box p={3} maxWidth={900} mx="auto">
      <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
        <LeafIcon color="success" sx={{ fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>
          My Quotes
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={3}>
        View your quote requests and track their progress.
      </Typography>

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{ mb: 3 }}
      >
        Request a new quote
      </Button>

      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Your quote requests
          </Typography>

          {isLoading ? (
            <CircularProgress size={24} />
          ) : requests.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary" gutterBottom>
                You haven't submitted any quote requests yet.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
              >
                Request your first quote
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell>Interest</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Requested</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((qr) => (
                    <TableRow key={qr.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {PRODUCT_INTEREST_LABELS[qr.product_interest]}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {qr.address_line_1}, {qr.city}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {qr.postcode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={QUOTE_REQUEST_STATUS_LABELS[qr.status]}
                          color={QUOTE_REQUEST_STATUS_COLORS[qr.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(qr.created_at).toLocaleDateString('en-GB')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Request dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request a Quote</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>
                {formError}
              </Alert>
            )}
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="First name"
                  value={form.first_name}
                  onChange={(e) => set('first_name', e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Last name"
                  value={form.last_name}
                  onChange={(e) => set('last_name', e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
              </Stack>
              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                required
                fullWidth
                size="small"
              />
              <TextField
                label="Phone (optional)"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                fullWidth
                size="small"
              />
              <Divider />
              <TextField
                label="Address line 1"
                value={form.address_line_1}
                onChange={(e) => set('address_line_1', e.target.value)}
                required
                fullWidth
                size="small"
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Town / City"
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                  required
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Postcode"
                  value={form.postcode}
                  onChange={(e) => set('postcode', e.target.value.toUpperCase())}
                  required
                  size="small"
                  inputProps={{ maxLength: 10 }}
                  sx={{ width: 140 }}
                />
              </Stack>
              <Divider />
              <TextField
                select
                label="I'm interested in…"
                value={form.product_interest}
                onChange={(e) => set('product_interest', e.target.value)}
                required
                fullWidth
                size="small"
              >
                {PRODUCT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Anything else we should know? (optional)"
                value={form.message}
                onChange={(e) => set('message', e.target.value)}
                multiline
                rows={3}
                fullWidth
                size="small"
                placeholder="Property age, current heating, budget…"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isLoading}
            >
              {createMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : 'Submit request'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default CustomerPortalPage;
